import {
    Component,
    ViewEncapsulation,
    OnInit,
    Input,
    Output,
    EventEmitter,
    SimpleChanges,
    OnChanges,
    ViewChild,
    AfterViewInit,
    ChangeDetectorRef,
} from '@angular/core';
import { MatLegacyTableDataSource as MatTableDataSource, MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ExceptionDaySettingsDialogComponent, ExceptionDaySettingsDialogInputData } from './exception-day-settings-dialog/exception-day-settings-dialog.component';
import { DateutilService, UTC } from 'app/shared/services/dateutil.service';
import { SliicerService } from 'app/shared/services/sliicer.service';
import { ExceptionDays } from 'app/shared/models/sliicer/settings';
import _ from 'lodash';
import { ChangesAction, UpdatesAction } from 'app/pages/sliicer/shared/components/updates-widget/updates-widget.models';
import { MatSort, Sort } from '@angular/material/sort';
import { compare, UiUtilsService } from 'app/shared/utils/ui-utils.service';



enum ExceptionDayColumns {
    dayGroupName = 'daysGroup',
    exceptionDate = 'date',
    action = 'action'
}

@Component({
    selector: 'ads-exception-day-settings',
    templateUrl: './exception-day-settings.component.html',
    styleUrls: ['./exception-day-settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ExceptionDaySettingsComponent implements OnInit, OnChanges, AfterViewInit {

    @ViewChild(MatSort) public sort: MatSort;

    @Input() public isStudyLocked: boolean;
    @Input() public changesAction: ChangesAction;
    @Output() public checkChanges: EventEmitter<null> = new EventEmitter();

    public ExceptionDayColumns = ExceptionDayColumns;
    public columns: Array<string> = [
        ExceptionDayColumns.dayGroupName,
        ExceptionDayColumns.exceptionDate,
        ExceptionDayColumns.action
    ];

    public UTC = UTC;
    public dataSource: MatTableDataSource<ExceptionDays>;
    public customerDateFormat: string;
    public initialFormValues: ExceptionDays[];
    public formValues: ExceptionDays[];

    constructor(
        private dateutilService: DateutilService,
        private uiUtils: UiUtilsService,
        private cdr: ChangeDetectorRef,
        private sliicerService: SliicerService,
        private dialog: MatDialog
    ) {

    }

    public ngOnInit() {
        this.customerDateFormat = this.dateutilService.getFormat();
    }

    public ngAfterViewInit() {
        this.sliicerService.studyDetailsData$.subscribe((caseStudyDetails) => {
            this.initialFormValues = [...caseStudyDetails.settings.exceptionDays ?? []];
            this.restorePreviousValue();
            this.sortData(this.sort);
            this.uiUtils.safeChangeDetection(this.cdr);
        })
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.changesAction && this.getChangeValue()) {
            if (this.changesAction.action === UpdatesAction.undoChanges) {
                this.restorePreviousValue();
                this.sortData(this.sort);
                this.checkChanges.emit();
            }
        }
    }

    public restorePreviousValue() {
        this.formValues = JSON.parse(JSON.stringify(this.initialFormValues));
        this.setupDataSource();
    }

    public getChangeValue(): boolean {
        // #43284 Need good performance on this one since it's called from HTML - compare not ordered arrays
        // #43284 Sort them by days since they are unique
        const assocArr: {[key: string]: string} = {};

        if(!this.initialFormValues && !this.formValues) return true;

        if(this.initialFormValues?.length !== this.formValues?.length) return true;

        for(const v of this.initialFormValues) {
            assocArr[v.exceptionDate] = v.dayGroupName;
        }

        for(const v of this.formValues) {
            if(!assocArr[v.exceptionDate] || !(assocArr[v.exceptionDate] === v.dayGroupName)) return true;
        }

        return false;
    }

    public getChanges(): ExceptionDays[] {
        return this.formValues;
    }

    private setupDataSource() {
        this.dataSource = new MatTableDataSource(this.formValues);
        this.dataSource.sort = this.sort;
    }

    sortData(sort: Sort) {
        const data = this.formValues;

        if (sort && sort.active && sort.direction) {
            const isAsc = this.sort.direction === 'asc';
            data.sort((a, b) => {
                switch (this.sort.active) {
                    case ExceptionDayColumns.dayGroupName:
                        return compare(a.dayGroupName, b.dayGroupName, isAsc);
                    case ExceptionDayColumns.exceptionDate:
                        return compare(a.exceptionDate, b.exceptionDate, isAsc);
                    default:
                        return 0;
                }
            });
        }
        this.dataSource.data = data;
    }

    private processChanges() {
        this.setupDataSource();
        this.sortData(this.sort);
        this.checkChanges.emit();
    }

    private addRow(row: ExceptionDays) {
        this.formValues.push(row);
        this.processChanges();
    }

    private updateRow(index: number, row: ExceptionDays) {
        this.formValues[index] = row;
        this.processChanges();
    }

    public remove(index: number) {
        this.formValues.splice(index, 1);
        this.processChanges();
    }

    public edit(index: number) {
        this.dialog.open<ExceptionDaySettingsDialogComponent, ExceptionDaySettingsDialogInputData, ExceptionDays>(ExceptionDaySettingsDialogComponent, {
            disableClose: true,
            data: {
                exceptionDays: this.formValues.filter((v, i) => i !== index),
                edit: this.formValues[index]
            }
        }).afterClosed().subscribe((result) => {
            if(result) {
                this.updateRow(index, result);
            }
        });
    }

    public add() {
        this.dialog.open<ExceptionDaySettingsDialogComponent, ExceptionDaySettingsDialogInputData, ExceptionDays>(ExceptionDaySettingsDialogComponent, {
            disableClose: true,
            data: {
                exceptionDays: this.formValues
            }
        }).afterClosed().subscribe((result) => {
            if(result) {
                this.addRow(result);
            }
        });
    }
}

